/**
 * useS3Upload.ts
 * 
 * Custom hook for integrating S3UploadContext with React Query
 * Provides easy access to upload context with cache invalidation on completion
 */

import { useCallback, useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useS3Upload as useS3UploadContext, type S3UploadContextType } from '../contexts/S3UploadContext';
import { useS3UploadManager } from '../contexts/S3UploadManagerContext';
import type { UploadOptions } from '../types/s3';

// ============================================================================
// Hook Return Type
// ============================================================================

/**
 * Return type for useS3Upload hook with React Query integration
 */
export interface UseS3UploadReturn extends S3UploadContextType {
  /** Invalidate S3 browser cache for a specific path */
  invalidateS3BrowserCache: (path: string) => Promise<void>;
  /** Start upload and automatically invalidate cache on completion */
  startUploadWithCacheInvalidation: (
    files: File[],
    currentPath: string,
    options?: UploadOptions
  ) => Promise<string>;
}

// ============================================================================
// useS3Upload Hook
// ============================================================================

/**
 * Custom hook for S3 upload context with React Query integration
 * 
 * Provides all upload context functionality plus automatic cache invalidation
 * on successful uploads.
 * 
 * Must be used within S3BrowserProvider and S3UploadProvider
 * 
 * @returns Upload context with cache invalidation utilities
 */
export const useS3Upload = (): UseS3UploadReturn => {
  const uploadContext = useS3UploadContext();
  const queryClient = useQueryClient();
  const { manager } = useS3UploadManager();

  /**
   * Invalidate S3 browser cache for a specific path
   * This triggers React Query to refetch the S3 file listing for the path
   */
  const invalidateS3BrowserCache = useCallback(
    async (path: string): Promise<void> => {
      await queryClient.invalidateQueries({
        queryKey: ['s3-browser', path],
      });
    },
    [queryClient]
  );

  /**
   * Start upload and automatically invalidate cache on completion
   * Registers a one-time callback to invalidate the browser cache
   * when the upload job completes successfully
   */
  const startUploadWithCacheInvalidation = useCallback(
    async (
      files: File[],
      currentPath: string,
      options?: UploadOptions
    ): Promise<string> => {
      // Check if manager is available
      if (!manager) {
        console.error('[S3Upload] Upload manager not available', {
          hasManager: !!manager,
          managerValue: manager,
        });
        throw new Error('Upload manager not initialized. Ensure S3UploadManagerWrapper is properly set up and S3Service is available.');
      }

      console.log('[S3Upload] Starting upload with cache invalidation', {
        fileCount: files.length,
        currentPath,
        hasManager: !!manager,
      });

      // Start the upload job using the manager
      // This will validate files and queue them for processing
      const jobId = await manager.startUploadJob(files, currentPath, options);

      console.log('[S3Upload] Upload job started', { jobId });

      // Set up a check for job completion
      // This is a simple polling approach - in production might use better signaling
      const checkInterval = setInterval(async () => {
        const job = uploadContext.getJob(jobId);
        
        if (job && job.status === 'completed') {
          // Job completed successfully - invalidate cache
          clearInterval(checkInterval);
          console.log('[S3Upload] Upload completed, invalidating cache', { jobId, currentPath });
          await invalidateS3BrowserCache(currentPath);
          
          // Also invalidate parent paths if needed
          if (currentPath && currentPath.includes('/')) {
            const parentPath = currentPath.substring(0, currentPath.lastIndexOf('/'));
            if (parentPath) {
              await invalidateS3BrowserCache(parentPath);
            }
          }
        } else if (job && (job.status === 'failed' || job.status === 'cancelled')) {
          // Job failed or cancelled - stop checking
          clearInterval(checkInterval);
          console.log('[S3Upload] Upload failed or cancelled', { jobId, status: job.status });
        }
      }, 500); // Check every 500ms

      // Clear interval after 5 minutes (max timeout)
      setTimeout(() => clearInterval(checkInterval), 5 * 60 * 1000);

      return jobId;
    },
    [manager, uploadContext, invalidateS3BrowserCache]
  );

  /**
   * Memoize the return value to prevent unnecessary re-renders
   */
  const value: UseS3UploadReturn = useMemo(
    () => ({
      ...uploadContext,
      invalidateS3BrowserCache,
      startUploadWithCacheInvalidation,
    }),
    [uploadContext, invalidateS3BrowserCache, startUploadWithCacheInvalidation]
  );

  return value;
};

export default useS3Upload;
